import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ExternalLink, ArrowLeft } from 'lucide-react';
interface SearchPageProps {
  onSelectCompany: () => void;
}
export function SearchPage({
  onSelectCompany
}: SearchPageProps) {
  const [searchValue, setSearchValue] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const fullText = 'Dunder Mifflin';
  // Typewriter effect
  useEffect(() => {
    if (!isTyping) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setSearchValue(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(interval);
        setIsTyping(false);
        setTimeout(() => setShowResult(true), 400);
      }
    }, 60);
    return () => clearInterval(interval);
  }, []);
  return <div className="px-8 py-6 max-w-5xl mx-auto" data-id="element-759">
      {/* Advanced Company Search */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6" data-id="element-760">
        <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-761">
          Advanced Company Search
        </h2>
        <div className="border-b border-gray-200 mb-6 mt-3" data-id="element-762" />

        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-763">
          {/* Company Name - pre-filled */}
          <div className="relative border border-gray-300 rounded-lg" data-id="element-764">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-765">
              Company Name *
            </label>
            <input type="text" value={searchValue} readOnly className="w-full px-4 py-3 bg-transparent text-gray-900 focus:outline-none" data-id="element-766" />
          </div>

          {/* Company Website */}
          <div className="relative border border-gray-300 rounded-lg" data-id="element-767">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-768">
              Company Website
            </label>
            <div className="px-4 py-3 text-gray-900" data-id="element-769">dundermifflin.com</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-770">
          {/* Country */}
          <div className="relative border border-gray-300 rounded-lg" data-id="element-771">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-772">
              Country
            </label>
            <div className="flex items-center justify-between px-4 py-3 text-gray-900" data-id="element-773">
              <span data-id="element-774">United States</span>
              <div className="flex items-center gap-1" data-id="element-775">
                <span className="text-gray-400 cursor-pointer" data-id="element-776">×</span>
                <span className="text-gray-400" data-id="element-777">▾</span>
              </div>
            </div>
          </div>

          {/* State */}
          <div className="relative border border-gray-300 rounded-lg" data-id="element-778">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-779">
              State
            </label>
            <div className="flex items-center justify-between px-4 py-3 text-gray-900" data-id="element-780">
              <span data-id="element-781">Pennsylvania</span>
              <span className="text-gray-400" data-id="element-782">▾</span>
            </div>
          </div>
        </div>

        <div className="mb-4" data-id="element-783">
          <div className="relative border border-gray-300 rounded-lg" data-id="element-784">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-785">
              Street Address
            </label>
            <div className="px-4 py-3 text-gray-900" data-id="element-786">1725 Slough Avenue</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-787">
          <div className="relative border border-gray-300 rounded-lg" data-id="element-788">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-789">
              City
            </label>
            <div className="px-4 py-3 text-gray-900" data-id="element-790">Scranton</div>
          </div>
          <div className="relative border border-gray-300 rounded-lg" data-id="element-791">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500" data-id="element-792">
              Postal Code
            </label>
            <div className="px-4 py-3 text-gray-900" data-id="element-793">18505</div>
          </div>
        </div>
      </div>

      {/* Search Result */}
      {showResult && <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 flex items-center gap-4" data-id="element-794">
          {/* Left accent bar */}
          <div className="w-1 self-stretch bg-green-500 rounded-full flex-shrink-0" data-id="element-795" />

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-796">
            <div className="flex items-center gap-2 mb-1" data-id="element-797">
              <h3 className="text-base font-bold text-gray-900 uppercase tracking-wide" data-id="element-798">
                DUNDER MIFFLIN PAPER COMPANY, INC.
              </h3>
              <ExternalLink className="w-4 h-4 text-indigo-500 flex-shrink-0" data-id="element-799" />
            </div>
            <p className="text-sm text-indigo-500 mb-2" data-id="element-800">
              1725 SLOUGH AVENUE, SCRANTON, PA, 18505
            </p>
            <div className="flex items-center gap-2" data-id="element-801">
              <span className="px-2.5 py-0.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full" data-id="element-802">
                Active
              </span>
              <span className="px-2.5 py-0.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full" data-id="element-803">
                HQ
              </span>
            </div>
          </div>

          {/* Go Here button with pulse */}
          <motion.button onClick={onSelectCompany} animate={{
        boxShadow: ['0 0 0 0 rgba(99, 102, 241, 0.4)', '0 0 0 10px rgba(99, 102, 241, 0)', '0 0 0 0 rgba(99, 102, 241, 0)']
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm flex-shrink-0 flex items-center gap-2" data-id="element-804">
            <ArrowLeft className="w-4 h-4" data-id="element-805" />
            Add & View
          </motion.button>
        </motion.div>}
    </div>;
}